import { useState } from 'react';
import { LuNetwork } from 'react-icons/lu';

import { AccountTaxTypeGroup } from '@ndvr/portfolio-api';

import { Card } from '@/components/ui/card';
import { getAllocationTypeLabel, useRequiredParams } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useDefaultAssetLocationPolicy } from 'views/portfolio/account-group-queries';
import { resolveAssetLocationPolicy } from 'views/portfolio/PortfolioPage/tabs/asset-location/resolve-asset-location-policy';
import { useAccountGroupAllocations } from 'views/portfolio/PortfolioPage/tabs/target-allocation/allocationUtils';

import { PolicyQueryError } from './PolicyQueryError';
import {
    PolicyTable,
    PolicyTableBody,
    PolicyTableCell,
    PolicyTableHead,
    PolicyTableHeader,
    PolicyTableRow,
} from './PolicyTable';

const preferenceLabels = ['1st Preference', '2nd Preference', '3rd Preference'];
const statusMessages = {
    loading: 'Loading asset location policy…',
    empty: 'No strategies in this allocation.',
};
const taxTypeLabels: Record<AccountTaxTypeGroup, string> = {
    TAX_DEFERRED: 'Tax-deferred',
    TAX_EXEMPT: 'Tax-exempt',
    TAXABLE: 'Taxable',
};

export const PolicyAssetLocation = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const profileQuery = useAccountGroupProfileWithAumIncluded(accountGroupGUID, { throwOnError: false });
    const [expanded, setExpanded] = useState(true);
    const profile = profileQuery.data;
    const coordinated = !!profile?.accountGroup.allocationGUID;
    const defaultsQuery = useDefaultAssetLocationPolicy({ enabled: coordinated, throwOnError: false });
    const allocationsQuery = useAccountGroupAllocations(profile, { enabled: coordinated, throwOnError: false });
    const { allocations } = allocationsQuery;
    const policies = resolveAssetLocationPolicy(
        allocations,
        defaultsQuery.data,
        profile?.accountGroup.assetLocationPolicy?.policyItems,
    );
    const hasError = profileQuery.isLoadingError || defaultsQuery.isLoadingError || allocationsQuery.isLoadingError;
    const pending = profileQuery.isPending || defaultsQuery.isPending || allocationsQuery.isPending;
    const status = hasError ? 'error' : pending ? 'loading' : policies.length ? 'ready' : 'empty';

    // InvestmentPolicyService permits asset location only with a portfolio-level allocation.
    // PortfolioPage gates its Asset Location tab on this same prerequisite.
    if (profile && !coordinated) return null;

    return (
        <Card className="gap-0 overflow-hidden rounded-[10px] border-stone-200 py-0 shadow-none">
            <h3 id="policy-asset-location-title">
                <button
                    type="button"
                    className="flex w-full cursor-pointer flex-wrap items-center gap-3.5 px-6 py-[18px] text-left"
                    aria-expanded={expanded}
                    aria-controls="policy-asset-location-details"
                    onClick={() => setExpanded(!expanded)}
                >
                    <LuNetwork aria-hidden="true" className="size-8 shrink-0 rounded-md bg-stone-900 p-2 text-white" />
                    <span className="text-cocoa-1000 flex-1 text-[15px] font-medium">Asset Location</span>
                    <span className="text-cocoa-600 shrink-0 text-xs">
                        {expanded ? 'Hide details' : 'Show details'}
                    </span>
                </button>
            </h3>
            {expanded && (
                <div id="policy-asset-location-details">
                    {status === 'error' ? (
                        <PolicyQueryError
                            message="Couldn’t load asset location policy."
                            queries={[profileQuery, defaultsQuery, allocationsQuery]}
                        />
                    ) : (
                        <PolicyTable
                            className="min-w-[760px] table-fixed"
                            containerClassName="rounded-none border-0 border-t"
                        >
                            <colgroup>
                                <col className="w-[82px]" />
                                <col className="w-[calc((100%-82px)*0.31818)]" />
                                <col />
                                <col />
                                <col />
                            </colgroup>
                            <PolicyTableHeader>
                                <PolicyTableRow>
                                    <PolicyTableHead className="h-11 py-0 font-semibold">Order</PolicyTableHead>
                                    <PolicyTableHead className="h-11 py-0 font-semibold">Strategy</PolicyTableHead>
                                    {preferenceLabels.map((label) => (
                                        <PolicyTableHead key={label} className="h-11 py-0 text-center font-semibold">
                                            {label}
                                        </PolicyTableHead>
                                    ))}
                                </PolicyTableRow>
                            </PolicyTableHeader>
                            <PolicyTableBody>
                                {status !== 'ready' ? (
                                    <PolicyTableRow>
                                        <PolicyTableCell
                                            colSpan={5}
                                            role="status"
                                            className="text-cocoa-500 py-8 text-center"
                                        >
                                            {statusMessages[status]}
                                        </PolicyTableCell>
                                    </PolicyTableRow>
                                ) : (
                                    policies.map((item, rowIndex) => (
                                        <PolicyTableRow key={item.growthAllocationType} className="h-16">
                                            <PolicyTableCell className="text-cocoa-500 py-2">
                                                {rowIndex + 1}
                                            </PolicyTableCell>
                                            <PolicyTableCell className="py-2 font-medium">
                                                {getAllocationTypeLabel(item.growthAllocationType)}
                                            </PolicyTableCell>
                                            {item.policy.map((taxType) => (
                                                <PolicyTableCell key={taxType} className="py-2 text-center">
                                                    <span className="inline-block rounded-[20px] border border-stone-300 px-3.5 py-1.5 text-sm leading-[21px] font-normal whitespace-nowrap">
                                                        {taxTypeLabels[taxType]}
                                                    </span>
                                                </PolicyTableCell>
                                            ))}
                                        </PolicyTableRow>
                                    ))
                                )}
                            </PolicyTableBody>
                        </PolicyTable>
                    )}
                </div>
            )}
        </Card>
    );
};
